/**
 * Responsibility: routing, and the one piece of cross-route state - the live
 * socket, which must survive navigation.
 *
 * The socket is owned here rather than inside the Live page so that an event
 * arriving while the operator is looking at Nodes still triggers auto-open. A
 * feed that only works on one tab is not a feed.
 *
 * The frame is the kit's AppShell, driven by `navigation.tsx`. It owns the
 * viewport and scrolls its main region, so pages render plain content and
 * never manage layout themselves.
 */
import { useCallback, useState } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { AccountMenu } from './auth/AccountMenu';
import {
  SidebarCollapseButton,
  SidebarCollapseScope,
} from './components/layout/SidebarCollapse';
import { useLocalStorage } from '@kit/hooks';
import { buildInfo } from './buildInfo';
import { AppShell } from '@kit/layout';

import { AnalysisOfflineBanner } from './components/layout/AnalysisOfflineBanner';
import { ConnectionChip } from '@kit/components/status';
import { brand, nav } from './navigation';
import { WatchlistPage } from './pages/WatchlistPage';
import { LivePage } from './pages/LivePage';
import { NewsPage } from './pages/NewsPage';
import { AlertsPage } from './pages/AlertsPage';
import { GradingPage } from './pages/GradingPage';
import { DiscoveryPage } from './pages/DiscoveryPage';
import { LikesPage } from './pages/LikesPage';
import { WatchingPage } from './pages/WatchingPage';
import { SearchPage } from './pages/SearchPage';
import { CollectionPage } from './pages/CollectionPage';
import { ScanPage } from './pages/ScanPage';
import { SettingsPage } from './pages/SettingsPage';
import { useLiveEvents } from './hooks/useLiveEvents';
import { useAutoOpen } from './hooks/useAutoOpen';
import type { AutoOpenOutcome } from './hooks/useAutoOpen';
import type { StockEvent } from './api/client';

const AUTO_OPEN_KEY = 'dropmon.autoOpen';

/**
 * Every page, with an outage notice above it.
 *
 * A layout route rather than a component each page imports: a banner somebody
 * has to remember to add is a banner that is missing from the page where it
 * mattered most.
 */
function WithOutageBanner() {
  return (
    <>
      <AnalysisOfflineBanner />
      <Outlet />
    </>
  );
}

export default function App() {
  // Off until the wizard verifies it, per section 6.9. The operator should
  // never be told this works before it has been observed working.
  const [autoOpen, setAutoOpen] = useState(
    () => window.localStorage.getItem(AUTO_OPEN_KEY) === 'verified',
  );
  const [lastOutcome, setLastOutcome] = useState<AutoOpenOutcome | null>(null);
  // Kept across visits: somebody who wants the sidebar out of the way
  // wants it out of the way tomorrow as well.
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage('dropmon.sidebar.collapsed', false);

  const { attempt } = useAutoOpen(autoOpen);

  const onEvent = useCallback(
    (event: StockEvent) => {
      if (event.offer.availability !== 'IN_STOCK') return;
      setLastOutcome(attempt(event));
    },
    [attempt],
  );

  const live = useLiveEvents(onEvent);

  const setAutoOpenVerified = useCallback((enabled: boolean) => {
    setAutoOpen(enabled);
    if (enabled) {
      window.localStorage.setItem(AUTO_OPEN_KEY, 'verified');
    } else {
      window.localStorage.removeItem(AUTO_OPEN_KEY);
    }
  }, []);

  return (
    <Routes>
      <Route
        element={
          <SidebarCollapseScope collapsed={sidebarCollapsed}>
            <AppShell
              nav={nav}
              brand={brand}
              buildInfo={buildInfo}
              topBarStatus={
                <ConnectionChip
                  connected={live.connected}
                  detail={live.connected ? 'Receiving events' : 'Reconnecting'}
                />
              }
              topBarActions={
                <>
                  <SidebarCollapseButton
                    collapsed={sidebarCollapsed}
                    onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                  />
                  <AccountMenu />
                </>
              }
            />
          </SidebarCollapseScope>
        }
      >
        {/* A nested layout, so the banner sits above whatever page is
            showing without editing AppShell - the kit is a faithful copy of
            an upstream template and has to stay re-syncable. */}
        <Route element={<WithOutageBanner />}>
        <Route path="/" element={<Navigate to="/watchlist" replace />} />
        <Route path="/watchlist" element={<WatchlistPage />} />
        <Route
          path="/live"
          element={
            <LivePage
              live={live}
              autoOpenEnabled={autoOpen}
              onAutoOpenChange={setAutoOpenVerified}
              lastOutcome={lastOutcome}
            />
          }
        />
        {/* Moved into Settings. Kept as a redirect rather than deleted:
            a bookmark that 404s is a worse answer than the panel's new
            home, and the cost is one line. */}
        <Route path="/nodes" element={<Navigate to="/settings" replace />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/alerts" element={<AlertsPage />} />
        <Route path="/grading" element={<GradingPage />} />
        <Route path="/discovery" element={<DiscoveryPage />} />
        <Route path="/likes" element={<LikesPage />} />
        <Route path="/watching" element={<WatchingPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/collection" element={<CollectionPage />} />
        <Route path="/scan" element={<ScanPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/watchlist" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}
